import {Fragment, useContext} from 'react'

import PropTypes from 'prop-types'

import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import LayoutGrid, {LayoutGridItem} from '@s-ui/react-layout-grid'
import MoleculeAccordion, {moleculeAccordionBehavior, MoleculeAccordionItem} from '@s-ui/react-molecule-accordion'
import AdDisplay from '@adv-mt/mt-ad-display'
import HeroBasic from '@adv-mt/mt-hero-basic'
import Layout from '@adv-mt/mt-layout-basic'
import MotorMarkdownWrapper from '@adv-mt/mt-markdown-wrapper/lib/Formalities'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import SectionBasic, {sectionBasicMargins} from '@adv-mt/mt-section-basic'
import IconAccordionCollapsed from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import IconAccordionExpanded from '@adv-mt/mt-svg-icons/lib/ChevronUp.js'
import MotorTrackingCriteo from '@adv-mt/mt-tracking-criteo'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {
  BASE_CLASS,
  SECTION_FIRST_TITLE,
  SECTION_FOURTH_TITLE,
  SECTION_SECOND_TITLE,
  SECTION_THIRD_TITLE
} from './config.js'
import {landingFormalitiesSecurityGuidelinesInitalProps} from './landingFormalitiesSecurityGuidelinesInitalProps.js'
import {useConfig} from './useConfig.js'

const LandingFormalitiesSecurityGuidelines = ({breadcrumb, helmet, loadThirdParties, pageName, tenant}) => {
  const {i18n} = useContext(Context)
  const {heroBasicHeaderProps} = useConfig()

  return (
    <Layout.Basic linkFactory={LinkFactory} isMarginless>
      <HeroBasic {...heroBasicHeaderProps} />
      <Helmet {...helmet} />
      <SectionBasic margin={sectionBasicMargins.xxl}>
        <div className={`${BASE_CLASS}-main`}>
          <MotorMarkdownWrapper tenant={tenant} documentName="formalitiesSecurityGuidelinesMain" />
        </div>
      </SectionBasic>
      <div className={`${BASE_CLASS}-section`}>
        <MoleculeAccordion
          as={Fragment}
          behavior={moleculeAccordionBehavior.MULTIPLE}
          defaultValues={[1]}
          headerIconCollapsed={<IconAccordionCollapsed size={ATOM_ICON_SIZES.medium} />}
          headerIconExpanded={<IconAccordionExpanded size={ATOM_ICON_SIZES.medium} />}
        >
          <LayoutGrid xxs={12}>
            <LayoutGridItem>
              <MoleculeAccordionItem
                as="div"
                content={
                  <div className={`${BASE_CLASS}-accordionContent`}>
                    <MotorMarkdownWrapper tenant={tenant} documentName="formalitiesSecurityGuidelinesSectionFirst" />
                  </div>
                }
                headerAs="h3"
                label={i18n.t(SECTION_FIRST_TITLE)}
                value={1}
              />
            </LayoutGridItem>
            <LayoutGridItem>
              <MoleculeAccordionItem
                as="div"
                content={
                  <div className={`${BASE_CLASS}-accordionContent`}>
                    <MotorMarkdownWrapper tenant={tenant} documentName="formalitiesSecurityGuidelinesSectionSecond" />
                  </div>
                }
                headerAs="h3"
                label={i18n.t(SECTION_SECOND_TITLE)}
                value={2}
              />
            </LayoutGridItem>
            <LayoutGridItem>
              <MoleculeAccordionItem
                as="div"
                content={
                  <div className={`${BASE_CLASS}-accordionContent`}>
                    <MotorMarkdownWrapper tenant={tenant} documentName="formalitiesSecurityGuidelinesSectionThird" />
                  </div>
                }
                headerAs="h3"
                label={i18n.t(SECTION_THIRD_TITLE)}
                value={3}
              />
            </LayoutGridItem>

            <LayoutGridItem>
              <MoleculeAccordionItem
                as="div"
                content={
                  <div className={`${BASE_CLASS}-accordionContent`}>
                    <MotorMarkdownWrapper tenant={tenant} documentName="formalitiesSecurityGuidelinesSectionFourth" />
                  </div>
                }
                headerAs="h3"
                label={i18n.t(SECTION_FOURTH_TITLE)}
                value={4}
              />
            </LayoutGridItem>
          </LayoutGrid>
        </MoleculeAccordion>
      </div>
      <div className={`${BASE_CLASS}-info`}>
        <MotorMarkdownWrapper tenant={tenant} documentName="formalitiesSecurityGuidelinesInfo" />
      </div>
      <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />
      {loadThirdParties && (
        <>
          <TrackingPageView pageName={pageName} />
          <MotorTrackingCriteo />
          <AdDisplay pageName={pageName} />
        </>
      )}
    </Layout.Basic>
  )
}

LandingFormalitiesSecurityGuidelines.displayName = 'LandingFormalitiesSecurityGuidelines'

LandingFormalitiesSecurityGuidelines.getInitialProps = landingFormalitiesSecurityGuidelinesInitalProps

LandingFormalitiesSecurityGuidelines.propTypes = {
  breadcrumb: PropTypes.array,
  helmet: PropTypes.object,
  loadThirdParties: PropTypes.bool,
  pageName: PropTypes.string,
  tenant: PropTypes.string
}

export default LandingFormalitiesSecurityGuidelines
